import React, { useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";

// Header
import Header from "./components/header/Header";

// Footer
import Footer from "./components/footer/Footer";

// Main Pages
import Home from "./pages/home";
import AboutUs from "./pages/about";
import Services from "./pages/services";
import ContactUs from "./pages/contact";
import Blog from "./pages/blog";
import ColdChainDeliveryInDubai from "./pages/cold-chain-delivery-in-dubai";
import ChilledCargoTransportTemperatureGuide from "./pages/chilled-cargo-transport-temperature-guide";
import GlobalVillageVendorDeliveryDubai from "./pages/global-village-vendor-delivery-dubai";

// Sub Services
import ChillerVans from "./pages/chiller-van";
import FreezerVans from "./pages/freezer-van";
import FreezerVanForRent from "./pages/freezer-van-for-rent";
import FreezerTruckForRent from "./pages/freezer-truck-for-rent";
import HighRoofDryVanForRent from "./pages/high-roof-dry-van-for-rent";
import HighRoof from "./pages/highroof-van";
import ChillerTruck from "./pages/chiller-truck";
import FreezerTruck from "./pages/freezer-truck";
import RefrigeratedTruck from "./pages/refrigerated-truck";
import RefrigeratedTruckForRent from "./pages/refrigerated-truck-for-rent";
import TruckRentalInFujairah from "./pages/truck-rental-in-fujairah";
import VanRentalInDubai from "./pages/van-rental-in-dubai";
import ChillerVanForRentInSharjah from "./pages/chiller-van-for-rent-in-sharjah";
import ChillerVanForRentInAbuDhabi from "./pages/chiller-van-for-rent-in-abu-dhabi";
import ChillerVanForRentInRasAlKhor from "./pages/chiller-van-for-rent-in-ras-al-khor";
import ChillerVanForRentInAlBarsha from "./pages/chiller-van-for-rent-in-al-barsha";
import ChillerVanForRentInAlQuoz from "./pages/chiller-van-for-rent-in-al-quoz";
import ChillerVanForRentInJebelAli from "./pages/chiller-van-for-rent-in-jebel-ali";
import ChillerVanForRentInDubaiInvestmentPark from "./pages/chiller-van-for-rent-in-dubai-investment-park";
import NotFound from "./pages/not-found";

function App() {
  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
      offset: 80,
      disableMutationObserver: false,
    });

    AOS.refresh();
  }, []);

  return (
    <HelmetProvider>
      <Header />
      <div className="site-wrapper">
        <main>
          <Routes>
            {/* ── Main pages ── */}
            <Route path="/" element={<Home />} />
            <Route path="/about-us" element={<AboutUs />} />
            <Route path="/services" element={<Services />} />
            <Route path="/all-services" element={<Navigate to="/services" replace />} />
            <Route path="/contact-us" element={<ContactUs />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/cold-chain-delivery-in-dubai" element={<ColdChainDeliveryInDubai />} />
            <Route path="/chilled-cargo-transport-temperature-guide" element={<ChilledCargoTransportTemperatureGuide />} />
            <Route path="/global-village-vendor-delivery-dubai" element={<GlobalVillageVendorDeliveryDubai />} />

            {/* ── Canonical service pages ── */}
            <Route path="/chiller-van-for-rent" element={<ChillerVans />} />
            <Route path="/chiller-truck-for-rent" element={<ChillerTruck />} />
            <Route path="/refrigerated-trucks-for-rent" element={<RefrigeratedTruckForRent />} />
            <Route path="/freezer-van-for-rent" element={<FreezerVanForRent />} />
            <Route path="/freezer-truck-for-rent" element={<FreezerTruckForRent />} />
            <Route path="/high-roof-dry-van-for-rent" element={<HighRoofDryVanForRent />} />
            <Route path="/truck-rental-in-fujairah" element={<TruckRentalInFujairah />} />
            <Route path="/van-rental-in-dubai" element={<VanRentalInDubai />} />
            <Route path="/chiller-van-for-rent-in-sharjah" element={<ChillerVanForRentInSharjah />} />
            <Route path="/chiller-van-for-rent-in-abu-dhabi" element={<ChillerVanForRentInAbuDhabi />} />
            <Route path="/chiller-van-for-rent-in-ras-al-khor" element={<ChillerVanForRentInRasAlKhor />} />
            <Route path="/chiller-van-for-rent-in-al-barsha" element={<ChillerVanForRentInAlBarsha />} />
            <Route path="/chiller-van-for-rent-in-al-quoz" element={<ChillerVanForRentInAlQuoz />} />
            <Route path="/chiller-van-for-rent-in-jebel-ali" element={<ChillerVanForRentInJebelAli />} />
            <Route path="/chiller-van-for-rent-in-dubai-investment-park" element={<ChillerVanForRentInDubaiInvestmentPark />} />

            {/* ── Redirects from old /services/ URLs ── */}
            <Route path="/services/chiller-vans" element={<Navigate to="/chiller-van-for-rent" replace />} />
            <Route path="/services/chiller-trucks" element={<Navigate to="/chiller-truck-for-rent" replace />} />
            <Route path="/services/freezer-vans" element={<Navigate to="/freezer-van-for-rent" replace />} />
            <Route path="/services/freezer-trucks" element={<Navigate to="/freezer-truck-for-rent" replace />} />

            {/* ── Redirects from other legacy URLs ── */}
            <Route path="/freezer-van" element={<Navigate to="/freezer-van-for-rent" replace />} />
            <Route path="/fleet-van" element={<Navigate to="/chiller-van-for-rent" replace />} />

            {/* ── Other service pages (routes kept, not in nav) ── */}
            <Route path="/services/high-roof-vans" element={<HighRoof />} />
            <Route path="/services/refrigerated-trucks" element={<RefrigeratedTruck />} />

            {/* ── Old blog article URLs redirect to the new blog hub (URLs may be indexed) ── */}
            <Route path="/chiller-van-rental-dubai-guide" element={<Navigate to="/blog" replace />} />
            <Route path="/benefits-refrigerated-transport-abu-dhabi" element={<Navigate to="/blog" replace />} />
            <Route path="/pharmaceutical-transport-compliance-uae" element={<Navigate to="/blog" replace />} />
            <Route path="/gps-temperature-monitoring-cold-chain" element={<Navigate to="/blog" replace />} />
            <Route path="/chiller-van-vs-freezer-truck" element={<Navigate to="/blog" replace />} />
            <Route path="/protecting-perishables-uae-summer" element={<Navigate to="/blog" replace />} />
            <Route path="/last-mile-delivery-dubai-restaurants" element={<Navigate to="/blog" replace />} />
            <Route path="/haccp-certified-transport-dubai-standards" element={<Navigate to="/blog" replace />} />

            {/* ── 404 catch-all ── */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
      </div>
      <Footer />
    </HelmetProvider>
  );
}

export default App;
